import { useWeb3React } from "@web3-react/core";
import { injectedConnector } from "@app/lib/connector";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import localForage from "localforage";
import { IS_CONNECTED } from "@app/shared/AppConstant";
import { logWalletConnected, setUserId } from "../analytics";

const ConnectorContext = createContext<any>(null);

export const ConnectorContextProvider = ({ children }: any) => {
  const { active, activate, deactivate, account, library } = useWeb3React();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isConnected, setConnected] = useState(false);

  const handleConnect = async () => {
    await activate(injectedConnector);
    await localForage.setItem(IS_CONNECTED, true);
    setConnected(true);
    setIsAuthorized(true);
  };

  const handleDisconnect = async () => {
    deactivate();
    setConnected(false);
    await localForage.removeItem(IS_CONNECTED);
  };

  const verifyAuthorization = useCallback(async () => {
    const isConnected = (await localForage.getItem(IS_CONNECTED)) === true;
    setConnected(isConnected);
    if (isConnected) {
      await activate(injectedConnector);
      if (account) {
        setUserId(account);
        logWalletConnected();
      }
    }
    setIsAuthorized(true);
  }, [account, activate]);

  useEffect(() => {
    verifyAuthorization();
  }, [active, verifyAuthorization]);

  const initializeConnector = useCallback(async () => {
    await verifyAuthorization();
    setIsLoading(false);
  }, [verifyAuthorization]);

  useEffect(() => {
    initializeConnector();
  }, [initializeConnector]);

  const provider = {
    connect: handleConnect,
    disconnect: handleDisconnect,
    account,
    library,
    loading: isLoading,
    authorized: isAuthorized && isConnected,
  };

  return <ConnectorContext.Provider value={provider}>{children}</ConnectorContext.Provider>;
};

export default function useConnector() {
  const context = useContext(ConnectorContext);

  if (context === undefined) {
    throw new Error("useConnector must be used within ConnectorContextProvider");
  }

  return context;
}
